import { useCallback, useEffect, useRef, useState } from 'react';
import { useContestStore } from '../../../contexts/mobx';
import type { ContestStats } from '../../../stores/contest';

export interface ContestStatsState {
    /** The last statistics read for this contest, kept while a refresh runs. */
    stats: ContestStats | null;
    isLoading: boolean;
    /** The last read failed: shown only when there is nothing else to show. */
    failed: boolean;
    refresh: () => void;
}

/**
 * The contest's statistics, read when the tab opens and on `refresh`. An
 * answer for a contest the reader has left, or older than the latest
 * request, is dropped.
 */
export function useContestStats(contestId: number): ContestStatsState {
    const store = useContestStore();
    const [stats, setStats] = useState<ContestStats | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const latest = useRef(0);

    const load = useCallback(async () => {
        latest.current += 1;
        const request = latest.current;
        setIsLoading(true);
        setFailed(false);
        const result = await store.fetchStats(contestId);
        if (request !== latest.current) {
            return;
        }
        if (result) {
            setStats(result);
        }
        setFailed(result === null);
        setIsLoading(false);
    }, [contestId, store]);

    useEffect(() => {
        setStats(null);
        load();
        return () => {
            latest.current += 1;
        };
    }, [load]);

    return { stats, isLoading, failed, refresh: load };
}
